import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Seo } from "@/components/site/Seo";
import { ph } from "@/lib/posthog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REVENUES = ["Under $2M", "$2M–$5M", "$5M–$10M", "$10M–$20M", "Over $20M"];
const SYSTEMS = ["Xero", "QuickBooks", "WorkflowMax", "More than one of these", "Something else or not sure"];

const VALID_SRC = /^[a-z0-9-]{1,32}$/;

const ApplyForm = ({ onSubmitted, source }) => {
  const [form, setForm] = useState({
    name: "", business: "", email: "", phone: "",
    revenue: "", system: "", decision: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const validate = () => {
    if (!form.name.trim()) return "Your name is required.";
    if (!form.business.trim()) return "Business name is required.";
    if (!/.+@.+\..+/.test(form.email)) return "Please enter a valid work email.";
    if (!form.revenue) return "Pick the revenue band that fits.";
    if (!form.system) return "Pick what you run your numbers on.";
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    const err = validate();
    if (err) { setError(err); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/apply`, {
        name: form.name.trim(),
        business: form.business.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim() || undefined,
        revenue: form.revenue,
        system: form.system,
        decision: form.decision.trim() || undefined,
        source: source || undefined,
      });
      ph("application_submitted", { revenue: form.revenue, system: form.system, source: source || "direct" });
      onSubmitted();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "We couldn't send your application. Try again in a moment.");
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="apply-form" noValidate data-testid="apply-form">
      <h2 className="apply-form-heading">Apply for a demo</h2>
      <p className="apply-form-sub">Tell us a little about the business. If it's a fit, we'll be in touch to book a time.</p>

      <label className="cv-field"><span>Your name</span>
        <input type="text" autoComplete="name" value={form.name} onChange={set("name")} placeholder="Sam Owner" required data-testid="apply-name" />
      </label>
      <label className="cv-field"><span>Business name</span>
        <input type="text" autoComplete="organization" value={form.business} onChange={set("business")} placeholder="Travel Trucks Pty Ltd" required data-testid="apply-business" />
      </label>
      <label className="cv-field"><span>Work email</span>
        <input type="email" autoComplete="email" value={form.email} onChange={set("email")} placeholder="sam@yourbusiness.com.au" required data-testid="apply-email" />
      </label>
      <label className="cv-field"><span>Phone <em>(optional)</em></span>
        <input type="tel" autoComplete="tel" value={form.phone} onChange={set("phone")} placeholder="04…" data-testid="apply-phone" />
      </label>
      <label className="cv-field"><span>Annual revenue</span>
        <select value={form.revenue} onChange={set("revenue")} required data-testid="apply-revenue">
          <option value="" disabled>Choose a range</option>
          {REVENUES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      </label>
      <label className="cv-field"><span>What do you run your numbers on?</span>
        <select value={form.system} onChange={set("system")} required data-testid="apply-system">
          <option value="" disabled>Choose one</option>
          {SYSTEMS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </label>
      <label className="cv-field"><span>What's a decision you're weighing up right now? <em>(optional)</em></span>
        <textarea
          rows={4}
          value={form.decision}
          onChange={set("decision")}
          placeholder="e.g. Should we hire another foreman this quarter? Can we afford the $90k van?"
          data-testid="apply-decision"
        />
      </label>

      {error ? <div className="cv-error" role="alert" data-testid="apply-error">{error}</div> : null}

      <button type="submit" className="pill" style={{ width: "100%", justifyContent: "center", marginTop: 6 }} disabled={submitting} data-testid="apply-submit">
        {submitting ? "Sending…" : "Apply for a demo"}
      </button>
      <p className="apply-fine">We read every application ourselves. No spam, no list — just a reply.</p>
    </form>
  );
};

const ApplySuccess = () => (
  <div className="apply-form apply-success" data-testid="apply-success">
    <div className="apply-seal" aria-hidden="true">
      <svg width="40" height="40" viewBox="0 0 28 28" fill="none">
        <path d="M6 14.5l5 5L22 8" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
    <h2 className="apply-form-heading center">Got it — thank you.</h2>
    <p className="apply-form-sub center" style={{ marginTop: 12 }}>
      Your application's in. We look at each one personally and we'll be in touch shortly to find a time. Keep an eye on your inbox.
    </p>
  </div>
);

const Apply = () => {
  const [submitted, setSubmitted] = useState(false);
  const [searchParams] = useSearchParams();
  const rawSrc = (searchParams.get("src") || "").trim().toLowerCase();
  const source = VALID_SRC.test(rawSrc) ? rawSrc : "";
  useEffect(() => { ph("apply_viewed", { source: source || "direct" }); }, [source]);

  return (
    <>
      <Seo
        title="Apply for a Clearview demo"
        description="Apply for a demo of Clearview — we sit down with you, connect read-only to your real numbers and give you a straight answer on a decision you're actually weighing up."
        path="/apply"
      />
      <Header />
      <section className="hero apply-hero" data-testid="apply-hero">
        <div className="glow" />
        <div className="container">
          <div className="apply-grid">
            {/* LEFT — pitch */}
            <div>
              <div className="eyebrow eyebrow-light">By application · A few owners at a time</div>
              <h1 style={{ marginTop: 18 }}>
                See it read a real business.<br />
                <span className="gtext">Yours.</span>
              </h1>
              <p className="sub" style={{ maxWidth: 520, marginTop: 22 }}>
                We sit down with you, connect Clearview to your real numbers, and you watch it give you a straight answer on a decision you're actually weighing up. No slides. No pitch.
              </p>

              <ul className="apply-points">
                <li>
                  <span className="apply-tick" aria-hidden="true">
                    <svg width="14" height="14" viewBox="0 0 18 18"><path d="M4 9.5 7.5 13 14 5" fill="none" stroke="url(#tg)" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  </span>
                  <div>
                    <h3>A real read, not a tour.</h3>
                    <p>Bring a decision you're stuck on and see what it says.</p>
                  </div>
                </li>
                <li>
                  <span className="apply-tick" aria-hidden="true">
                    <svg width="14" height="14" viewBox="0 0 18 18"><path d="M4 9.5 7.5 13 14 5" fill="none" stroke="url(#tg)" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  </span>
                  <div>
                    <h3>Read-only, always.</h3>
                    <p>Connects to Xero, QuickBooks or WorkflowMax — it can't move a cent or change your books.</p>
                  </div>
                </li>
                <li>
                  <span className="apply-tick" aria-hidden="true">
                    <svg width="14" height="14" viewBox="0 0 18 18"><path d="M4 9.5 7.5 13 14 5" fill="none" stroke="url(#tg)" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  </span>
                  <div>
                    <h3>We choose who we take on.</h3>
                    <p>Built for $2–20M owner-operators, onboarded a few at a time.</p>
                  </div>
                </li>
              </ul>

              <div className="chips">
                <span>Plain English</span><span>Read-only</span><span>No lock-in</span><span>Australian-built</span>
              </div>
            </div>

            {/* RIGHT — form */}
            <div className="apply-card-wrap">
              {submitted ? <ApplySuccess /> : <ApplyForm onSubmitted={() => setSubmitted(true)} source={source} />}
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
};

export default Apply;
